/**
 * **A port of `legacy/frontend/src/screens/AttendanceRow.tsx`, whole but for its `:3`.** F-014 T4:
 * one member's row in a roster, with the pair «здесь / нет» and the mark sheet behind it.
 * `PastRoster` (T6) is its first caller; F-012's live panel is its second.
 *
 * Their `:3` imports `AttendanceStatus` and `LiveRow` from `../api`. Both are server shapes, so here
 * they come from `@contracts/index` (ADR-0011); `app/src/api.ts` does not re-export them —
 * `ProfileFields.tsx`'s and `PartSegments.tsx`'s answer. That is the one line that differs. Their
 * `:1-2` and their `:4` to the end are theirs byte for byte, and `scripts/ported-partial.txt` holds
 * both regions.
 *
 * **Whole, including what only the live caller reaches** (build spec §0.2 Q1): the sheet label
 * `Отметка · {name}` (their `:379`), «Причина: …» (`:456-458`) and `editable`'s default `true`
 * (`:77`). They are one ternary, one conjunct and one default, not separable code, and cutting them
 * would rewrite three expressions rather than leave a hole. AC-33 holds them; their parity on the
 * live panel is F-012's.
 */
import { useEffect, useState } from 'react';

import type { AttendanceStatus, LiveRow } from '@contracts/index';
import { ChevronRightIcon, WarningIcon } from '../icons';
import { formatTime, voicePartLetter } from '../format';
import Sheet from './Sheet';
import { TimeWheel } from './TimeWheel';

/**
 * How long the pressed half keeps its own colour before the pair leaves.
 *
 * The answer to a tap is the verdict, and the verdict is on the other side of
 * the row: without this the half under the finger was gone before the finger
 * was, and what the regent saw acknowledge his press was nothing at all. Ninety
 * is the same beat a press already takes to go down.
 */
const MARK_HOLD_MS = 90;
/** …and then it goes, by its own width. */
const MARK_OUT_MS = 140;

/* One noun each, and no participle: «Опоздал» and «Не был» agree in gender and
   were printed beside women's names. See «Attendance vocabulary» in
   `DESIGN.md`. */
const STATUSES: [AttendanceStatus, string][] = [
  ['on_time', 'Вовремя'],
  ['late', 'Опоздание'],
  ['absent', 'Пропуск'],
];

/**
 * The verdict on one person, compressed to the width of a column.
 *
 * «18:01 вовремя» · «18:12 +12» · «пропуск · рег» · «опоздание · рег». The time
 * comes first because down forty rows those read as a column, and it is there
 * only when somebody measured it: a mark the regent set by hand carries the
 * moment his finger landed, not the moment anybody arrived, so it says «рег» in
 * that place instead. The decision is of 2026-08-20 and this is where it shows.
 *
 * The words are the app's one attendance vocabulary and not this row's own:
 * «ок» was the only English-flavoured verdict left, and «не был» and «опоздал»
 * agree in gender.
 */
function statusText(row: LiveRow): string {
  const manual = row.source === 'manual';
  // An absence has no arrival by definition, so a time is never right on it.
  if (row.status === 'absent') return manual ? 'пропуск · рег' : 'пропуск';
  const measured = !manual || row.late_minutes !== null;
  const time = measured && row.marked_at !== null ? formatTime(row.marked_at) : null;
  const word =
    row.status === 'late'
      ? row.late_minutes === null
        ? 'опоздание'
        : `+${row.late_minutes}`
      : 'вовремя';
  if (time !== null) return `${time} ${word}`;
  return manual ? `${word} · рег` : word;
}

/** The one-letter voice part after the name, and the qualifiers under it. */
function excusedNote(row: LiveRow): string | null {
  return row.is_excused ? 'уважительно' : null;
}

/**
 * A member's row, with one-tap status switching and the sheet behind it.
 *
 * The live panel and the history show the same roster and edit it through the
 * same transitions, so one row component serves both screens. Exactly one thing
 * differs: a rehearsal older than the edit window has no toggles — the server
 * would refuse the edit, and showing buttons that are certain to be refused
 * means promising the impossible.
 */
export default function AttendanceRow({
  row,
  expanded,
  busy,
  editable = true,
  onToggle,
  onSetStatus,
  onToggleExcuse,
  arrivalTime,
  excuseNote,
  failure = null,
}: {
  row: LiveRow;
  /** Whether this row's mark sheet is the one that is up. */
  expanded: boolean;
  busy: boolean;
  editable?: boolean;
  onToggle: () => void;
  onSetStatus: (status: AttendanceStatus) => void;
  onToggleExcuse: () => void;
  /**
   * Entering the time somebody actually came, offered on a row already marked
   * «Опоздание». Optional to the regent as well as to this component: left empty,
   * the mark stays «опоздание» with no minutes, which is the honest answer when
   * nobody looked at a clock.
   */
  arrivalTime?: {
    value: string;
    dirty: boolean;
    /**
     * When the gathering began, which is where the wheel stands before anybody
     * has moved it: whoever is being marked late came in after that, so it is
     * the nearest to the truth an unanswered wheel can be.
     */
    start: string;
    onChange: (value: string) => void;
    onSave: () => void;
  };
  /**
   * Editing of the reason, offered only where the regent is not in a hurry.
   * Absent on the live panel: during a rehearsal he goes down the roster with a
   * thumb, and a text field in that list would slow the one flow that has to be
   * fast. The reason he typed earlier still shows there, read-only.
   *
   * It is also how the sheet knows which of the two screens it is standing on —
   * the same optional property that has told them apart all along.
   */
  excuseNote?: {
    value: string;
    dirty: boolean;
    onChange: (value: string) => void;
    onSave: () => void;
  };
  /**
   * What was refused about this row, read where the tap was made: inside the
   * sheet the tap happened in. It used to stand under the whole list, so a mark
   * corrected in the third row answered below the fortieth.
   */
  failure?: string | null;
}) {
  // Two flags, and both are a second line rather than an icon: a glyph the
  // regent has to remember the meaning of is no use at the speed this list is
  // read. The conflict is the louder of them - somebody tried to check in after
  // being written down as absent - and it is the only one that asks for a
  // decision.
  const flag = row.has_conflict ? (
    <span className="live-flag" data-status="absent">
      ⚠ код после «пропуска»
    </span>
  ) : row.ip_mismatch ? (
    <span className="live-flag" data-status="late">
      ⚑ другой адрес
    </span>
  ) : null;

  /**
   * Whether the pair «здесь / нет» is still on the row.
   *
   * It outlives the mark by one leaving run, which is the shape `Unfold` and
   * `Sheet` both have and for the same reason: what takes the pair away is the
   * verdict arriving, and a row that stops rendering it in that frame leaves
   * nothing to animate. The two are one event — the finger pressed a half and
   * the answer took its place — so the pair going and the verdict coming are
   * the two halves of one movement rather than a swap between two frames.
   */
  const marked = row.status !== null;
  const [pair, setPair] = useState(!marked);
  useEffect(() => {
    if (!marked) {
      setPair(true);
      return;
    }
    if (!pair) return;
    const timer = window.setTimeout(() => setPair(false), MARK_HOLD_MS + MARK_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [marked, pair]);

  const verdict =
    row.status === null ? null : (
      <span className="live-status" data-status={row.status}>
        {statusText(row)}
      </span>
    );

  /**
   * The name, and under it whatever else the row has to say.
   *
   * The verdict used to stand opposite the name, in the same column as the
   * «здесь / нет» it replaces the moment a mark is set - so the two states of a
   * row differed by the outline of a button and by nothing else, at 1.74:1
   * against the page. Down forty rows, one-handed, in a half-dark loft, that is
   * a mark read off the wrong row, and a mark read off the wrong row writes an
   * untruth about a person. Since 2026-08-28 a marked row has two lines on the
   * left where an unmarked one has one and a pair of buttons. The shapes differ,
   * not only the edges.
   */
  const personWith = (showVerdict: boolean) => {
    const said = showVerdict ? verdict : null;
    return (
      <span className="live-body">
        <span className="live-name">
          {row.display_name}
          {/* The part rather than a longer name: it is already in the response,
              and two people called Анна in a choir are told apart by singing
              soprano and alto, not by a surname the roster may not have. */}
          {' '}
          <span className="live-part">{voicePartLetter(row.voice_part)}</span>
          {excusedNote(row) !== null && <span className="live-part"> · {excusedNote(row)}</span>}
        </span>
        {(said !== null || flag !== null) && (
          <span className="live-under">
            {said}
            {said !== null && flag !== null && <span className="live-under-sep"> · </span>}
            {flag}
          </span>
        )}
      </span>
    );
  };

  if (!editable) {
    return <li className="live-row live-row--attend">{personWith(true)}</li>;
  }

  return (
    <li
      className={
        marked && pair
          ? 'live-row live-row--markable live-row--attend live-row--marking'
          : 'live-row live-row--markable live-row--attend'
      }
    >
      {/* Nobody has touched this row yet, so the marks are in it: the regent is
          going down the list with one thumb and the commonest verdict has to be
          reachable without opening anything. */}
      {pair ? (
        <>
          <button className="live-tap" data-press="wash" onClick={onToggle}>
            {personWith(marked)}
            {marked && <ChevronRightIcon size={20} quiet />}
          </button>
          {/* Two and not three since 2026-08-21. «ок / +мин / нет» were 22px
              with 5 between them, so their 44px hit areas overlapped each
              other - and a miss on a live rehearsal writes the wrong thing
              about a person. «+мин» was also the odd one out: it is not a
              verdict but a verdict plus a measurement, and it is still in the
              sheet where the minutes are picked.

              A pair about the room rather than the three about the record:
              the buttons set a fact - is this person here - while «вовремя /
              опоздание / пропуск» is what the journal ends up saying. Both
              sets are neutral in gender and in tense, which «был» and «не был»
              were not: they were printed beside women's names, and beside a
              gathering that is already over. */}
          <span className={marked ? 'live-marks live-marks--going' : 'live-marks'}>
            <button
              className="live-mark live-mark--on_time"
              data-press="wash"
              disabled={busy || marked}
              onClick={() => onSetStatus('on_time')}
            >
              здесь
            </button>
            <button
              className="live-mark"
              data-press="wash"
              disabled={busy || marked}
              onClick={() => onSetStatus('absent')}
            >
              нет
            </button>
          </span>
        </>
      ) : (
        /* A verdict, and the door to correcting it. The chevron arrives with
           the verdict and not before: on an unmarked row the right-hand side is
           the pair, and there is nothing to correct there anyway. Pointing
           right, which since 2026-09-03 also means a sheet - «deeper», rather
           than «a panel opening below». */
        <button className="live-tap" data-press="wash" onClick={onToggle}>
          {personWith(true)}
          <ChevronRightIcon size={20} quiet />
        </button>
      )}

      <MarkSheet
        row={row}
        open={expanded}
        busy={busy}
        failure={failure}
        onClose={onToggle}
        onSetStatus={onSetStatus}
        onToggleExcuse={onToggleExcuse}
        arrivalTime={arrivalTime}
        excuseNote={excuseNote}
      />
    </li>
  );
}

/**
 * Correcting one person's mark, over the screen the row is on.
 *
 * This was a card the row unfolded into, and what was in it was a form: a
 * status, a wheel of minutes, a reason with a keyboard under it. A form arrives
 * as a sheet — the rule of three containers, which the same card broke twice
 * over by also standing inside a scrolling list of forty names and moving it
 * under the thumb that opened it. `ProfileFields` left the roster row for a
 * sheet on the same grounds.
 *
 * It comes free of two other things. The regent's panel polls every two seconds,
 * and the open row had to be held in a state of its own so a redraw could not
 * close it under his finger; a sheet stands over the screen and survives the
 * redraw by itself. And the list stops moving at all rather than moving less.
 */
function MarkSheet({
  row,
  open,
  busy,
  failure,
  onClose,
  onSetStatus,
  onToggleExcuse,
  arrivalTime,
  excuseNote,
}: {
  row: LiveRow;
  open: boolean;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onSetStatus: (status: AttendanceStatus) => void;
  onToggleExcuse: () => void;
  arrivalTime?: {
    value: string;
    dirty: boolean;
    start: string;
    onChange: (value: string) => void;
    onSave: () => void;
  };
  excuseNote?: {
    value: string;
    dirty: boolean;
    onChange: (value: string) => void;
    onSave: () => void;
  };
}) {
  // The reason field is the history's and not the live panel's, so its presence
  // is also the answer to which screen this is standing on.
  const past = excuseNote !== undefined;
  const timing = row.status === 'late' && arrivalTime !== undefined;
  const reason = row.status === 'absent' && row.is_excused && excuseNote !== undefined;

  /* At most one of the two ever stands here: a time belongs to «Опоздание» and
     a reason to an excused «Пропуск», and no mark is both. The refusal goes
     directly above whichever it is, because that is the pair this slot exists
     for - and the sheet stays up, so what was refused can be tried again where
     it was tried. */
  const foot =
    failure !== null || timing || reason ? (
      <>
        {failure !== null && <p className="failure">{failure}</p>}
        {timing && (
          <button
            className="btn btn--quiet"
            disabled={busy || !arrivalTime.dirty}
            onClick={arrivalTime.onSave}
          >
            Сохранить время
          </button>
        )}
        {reason && (
          <button
            className="btn btn--quiet"
            disabled={busy || !excuseNote.dirty}
            onClick={excuseNote.onSave}
          >
            Сохранить причину
          </button>
        )}
      </>
    ) : undefined;

  return (
    <Sheet
      open={open}
      label={past ? `Отметка · рег · ${row.display_name}` : `Отметка · ${row.display_name}`}
      onClose={onClose}
      foot={foot}
    >
      <p className="card-title">{row.display_name}</p>

      {row.has_conflict && (
        <p className="notice notice--danger">
          <WarningIcon size={18} />
          Отметка по коду пришла позже отметки об отсутствии. Если человек был — верни отметку.
        </p>
      )}

      {/* All three carry the same weight: this is a choice, and the chosen one
          is filled with the accent rather than its status colour. */}
      <div className="seg">
        {STATUSES.map(([status, label]) => (
          <button
            key={status}
            className={row.status === status ? 'btn btn--selected' : 'btn btn--outline'}
            disabled={busy}
            onClick={() => onSetStatus(status)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* A time and not a date with it: the rehearsal's own day is known, and
          asking for it again would be asking the regent to retype what the app
          already has. Saved by its own button, because the status is already in
          by then — the tap on «Опоздание» wrote it — and this only adds the
          minutes to it. */}
      {timing && (
        <div className="field">
          <span>Если знаешь время прихода — посчитаем опоздание</span>
          {/* Minute by minute, unlike every other wheel in the app: the
              lateness is counted off this figure, and a wheel that went in
              fives would turn seven minutes into five or ten and make the
              number in the journal untrue. */}
          <TimeWheel
            label="Время прихода"
            value={arrivalTime.value === '' ? null : arrivalTime.value}
            fallback={formatTime(arrivalTime.start)}
            step={1}
            onChange={arrivalTime.onChange}
          />
        </div>
      )}

      {/* Only on an absence, because that is the only place the flag means
          anything: its one reader, the excused count, tests for absent first
          (domain/stats.py). The streak no longer reads it at all - any absence
          resets it, excused or not. Hung on somebody who came, the flag changed
          no number anywhere and left the regent looking for the effect of a
          button that had none. */}
      {row.status === 'absent' && (
        <button className="btn btn--quiet" disabled={busy} onClick={onToggleExcuse}>
          {row.is_excused ? 'Убрать уважительную' : 'Уважительная причина'}
        </button>
      )}

      {/* The reason is written for the regent by the regent - a month later
          "уважительно" alone does not say whether somebody was ill or away. It
          goes nowhere near the singer's own screen: a note about a person is not
          a note to them. */}
      {reason && (
        <label className="field">
          <span>Почему — чтобы вспомнить потом</span>
          <input
            placeholder="Например: болеет"
            maxLength={500}
            value={excuseNote.value}
            onChange={(event) => excuseNote.onChange(event.target.value)}
          />
        </label>
      )}
      {row.status === 'absent' && row.is_excused && !past && row.excuse_note !== null && (
        <p className="hint">Причина: {row.excuse_note}</p>
      )}

      {row.ip_mismatch && (
        <p className="hint">
          Флажок — отметка пришла с другого адреса, чем у остальных. Это сигнал, а не запрет.
        </p>
      )}

      {/* The flag does not undo the fact: it shows as its own number rather than
          improving the main figure. */}
      <p className="hint">
        «Пропуск» снимает отметку, но не стирает строку: человек останется в знаменателе и не
        сможет отметиться заново.
      </p>
    </Sheet>
  );
}
